import React, { useState, useEffect, useRef } from 'react';
import './AdminHeader.css';
import logoGreen from '../sweetspot-logo-green.png';

// Store info structure grouped by location
export interface StoreInfo {
  id: string;           // e.g., "west-hartford"
  city: string;         // e.g., "West Hartford"
  state: string;        // e.g., "CT"
  stateFull: string;    // e.g., "connecticut"
  hasMenuTypes: {
    med: boolean;
    rec: boolean;
  };
  documentIds: {
    med?: string;       // Legacy menu ID for med menu
    rec?: string;       // Legacy menu ID for rec menu
  };
}

// Build store list from existing storeItems data
export const buildStoreList = (): StoreInfo[] => {
  const storeMap = new Map<string, StoreInfo>();

  // Store items data (from Header.tsx)
  const storeItems = [
    { storeId: '5796', name: 'West Hartford, CT - MED', state: 'connecticut', city: 'west-hartford', menuType: 'medical' },
    { storeId: '5797', name: 'West Hartford, CT - REC', state: 'connecticut', city: 'west-hartford', menuType: 'recreational' },
    { storeId: '857', name: 'Olney, MD - MED', state: 'maryland', city: 'olney', menuType: 'medical' },
    { storeId: '5440', name: 'Olney, MD - REC', state: 'maryland', city: 'olney', menuType: 'recreational' },
    { storeId: '4855', name: 'Voorhees, NJ - MED', state: 'new-jersey', city: 'voorhees', menuType: 'medical' },
    { storeId: '5377', name: 'Voorhees, NJ - REC', state: 'new-jersey', city: 'voorhees', menuType: 'recreational' },
    { storeId: '696002abeb54a2091b709e0f', name: 'Hamilton, NJ - REC', state: 'new-jersey', city: 'hamilton', menuType: 'recreational' },
    { storeId: '6247', name: 'River Edge, NJ - REC', state: 'new-jersey', city: 'river-edge', menuType: 'recreational' },
    { storeId: '6248', name: 'Mt Olive, NJ - REC', state: 'new-jersey', city: 'mount-olive', menuType: 'recreational' },
    { storeId: '4545', name: 'Exeter, RI - MED', state: 'rhode-island', city: 'exeter', menuType: 'medical' },
    { storeId: '4996', name: 'Exeter, RI - REC', state: 'rhode-island', city: 'exeter', menuType: 'recreational' },
    { storeId: '6145', name: 'Essex Junction, VT - REC', state: 'vermont', city: 'essex-junction', menuType: 'recreational' },
    { storeId: '6158', name: 'Stamford, CT - MED', state: 'connecticut', city: 'stamford', menuType: 'medical' },
    { storeId: '6159', name: 'Stamford, CT - REC', state: 'connecticut', city: 'stamford', menuType: 'recreational' }
  ];

  // State abbreviation map
  const stateAbbrev: Record<string, string> = {
    'connecticut': 'CT',
    'maryland': 'MD',
    'new-jersey': 'NJ',
    'rhode-island': 'RI',
    'vermont': 'VT'
  };

  // Group by city
  storeItems.forEach(store => {
    const key = `${store.state}/${store.city}`;
    const cityName = store.name.split(',')[0]; // Extract city name

    if (!storeMap.has(key)) {
      storeMap.set(key, {
        id: store.city,
        city: cityName,
        state: stateAbbrev[store.state] || store.state.toUpperCase(),
        stateFull: store.state,
        hasMenuTypes: {
          med: false,
          rec: false
        },
        documentIds: {}
      });
    }

    const storeInfo = storeMap.get(key)!;
    if (store.menuType === 'medical') {
      storeInfo.hasMenuTypes.med = true;
      storeInfo.documentIds.med = store.storeId;
    } else {
      storeInfo.hasMenuTypes.rec = true;
      storeInfo.documentIds.rec = store.storeId;
    }
  });

  return Array.from(storeMap.values());
};

// Group stores by state for dropdown
export const groupStoresByState = (stores: StoreInfo[]): Map<string, StoreInfo[]> => {
  const grouped = new Map<string, StoreInfo[]>();

  stores.forEach(store => {
    const stateName = store.stateFull.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');

    if (!grouped.has(stateName)) {
      grouped.set(stateName, []);
    }
    grouped.get(stateName)!.push(store);
  });

  // Sort states alphabetically
  return new Map([...grouped.entries()].sort());
};

interface AdminHeaderProps {
  stores: StoreInfo[];
  selectedStore: StoreInfo | null;
  selectedMenuType: 'med' | 'rec' | null;
  onSelectStore: (store: StoreInfo) => void;
  userEmail?: string;
  onLogout: () => void;
}

const AdminHeader: React.FC<AdminHeaderProps> = ({
  stores,
  selectedStore,
  selectedMenuType,
  onSelectStore,
  userEmail,
  onLogout
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const groupedStores = groupStoresByState(stores);

  const getDisplayText = (): string => {
    if (!selectedStore) {
      return 'CONFIGURE LOCATION';
    }

    let text = `${selectedStore.city}, ${selectedStore.state}`;
    if (selectedMenuType) {
      text += ` ${selectedMenuType.toUpperCase()}`;
    }
    return text;
  };

  const handleStoreClick = (store: StoreInfo) => {
    onSelectStore(store);
    setIsDropdownOpen(false);
  };

  return (
    <header className="admin-header">
      <div className="admin-header-left">
        <div className="admin-logo-container">
          <img src={logoGreen} alt="Sweetspot" className="admin-logo" />
        </div>
      </div>

      <div className="admin-header-right">
        <div className="admin-location-container" ref={dropdownRef}>
          <button
            className={`admin-location-button ${isDropdownOpen ? 'active' : ''}`}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            {getDisplayText()}
            <span className="admin-dropdown-arrow">▼</span>
          </button>

          {isDropdownOpen && (
            <div className="admin-location-dropdown">
              {Array.from(groupedStores.entries()).map(([stateName, stateStores]) => (
                <div key={stateName} className="admin-dropdown-group">
                  <div className="admin-dropdown-state">{stateName.toUpperCase()}</div>
                  {stateStores.map(store => (
                    <div
                      key={store.id}
                      className={`admin-dropdown-item ${selectedStore?.id === store.id ? 'selected' : ''}`}
                      onClick={() => handleStoreClick(store)}
                    >
                      {store.city}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="admin-user-section">
          {userEmail && (
            <span className="admin-user-email">{userEmail}</span>
          )}
          <button className="admin-logout-button" onClick={onLogout}>
            LOG OUT
          </button>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
